'use client';

import { useEffect, useId, useRef, useState, type FocusEvent, type MouseEvent, type RefObject } from 'react';

import { usePrefiereMovimientoReducido } from '@/components/asistente/movimientoReducido';
import { CLAVES_BIBLIOTECA, GEOMETRIA_ESCRITORIO, GEOMETRIA_MOVIL, type ClaveBiblioteca, type GeometriaBiblioteca } from '@/lib/marketing/geometriaBiblioteca';

import EscenarioBibliotecaEscritorio from './EscenarioBibliotecaEscritorio';
import EscenarioBibliotecaMovil from './EscenarioBibliotecaMovil';
import { alternarFijo, CENTRO_ESCRITORIO, CENTRO_MOVIL, funcionEnPunto, indiceDestacado, puntoEnDiseno, resolverActiva, type Punto } from './estadoBiblioteca';
import { TEXTOS_FUNCIONES, TEXTOS_SECCION } from './textosBiblioteca';

export interface PropiedadesBiblioteca {
    /** La función que la página de funcionalidad destaca; omitida o «ninguno» en la landing (D-13). */
    destacado?: ClaveBiblioteca | 'ninguno';
    idSeccion?: string;
}

const ESCENARIOS: Record<string, { geometria: GeometriaBiblioteca; centro: Punto; ancho: number }> = {
    escritorio: { geometria: GEOMETRIA_ESCRITORIO, centro: CENTRO_ESCRITORIO, ancho: 1240 },
    movil: { geometria: GEOMETRIA_MOVIL, centro: CENTRO_MOVIL, ancho: 326 },
};

// La función bajo un clic: la del botón si se pulsa uno; si no, el sector bajo el punto en el
// árbol que recibió el clic; fuera de los dos escenarios, ninguna.
function funcionDelClic(objetivo: EventTarget | null, clienteX: number, clienteY: number): number {
    if (!(objetivo instanceof Element)) return -1;
    const boton = objetivo.closest('[data-fn]');
    if (boton) return Number(boton.getAttribute('data-fn'));
    const arbol = objetivo.closest<HTMLElement>('[data-arbol]');
    const escenario = arbol && ESCENARIOS[arbol.dataset.arbol ?? ''];
    const caja = arbol?.querySelector('[data-bib]');
    if (!escenario || !caja || !caja.contains(objetivo)) return -1;
    const punto = puntoEnDiseno(clienteX, clienteY, caja.getBoundingClientRect(), escenario.ancho);
    return funcionEnPunto(escenario.geometria, punto.x - escenario.centro.x, punto.y - escenario.centro.y);
}

// La función enfocada cuenta como activa solo si el foco es visible (teclado), como enfocar()
// del tablero: un clic con ratón también enfoca y no debe encender nada por eso.
function funcionConFocoVisible(objetivo: EventTarget | null): number {
    if (!(objetivo instanceof Element)) return -1;
    const boton = objetivo.closest('[data-fn]');
    return boton && boton.matches(':focus-visible') ? Number(boton.getAttribute('data-fn')) : -1;
}

function Cabecera({ idTitulo, destacado }: { idTitulo: string; destacado: number }) {
    const conDestacado = destacado >= 0;
    const titulo = conDestacado ? TEXTOS_FUNCIONES[CLAVES_BIBLIOTECA[destacado]].titulo : TEXTOS_SECCION.tituloReposo;
    return (
        <div className="relative flex flex-col gap-3 text-center lg:items-center lg:gap-3.5">
            <h2 id={idTitulo} className="m-0 font-display text-[30px] font-bold leading-[1.12] tracking-[-0.02em] text-balance lg:max-w-[880px] lg:text-[40px]">
                {titulo}
            </h2>
            <p className="m-0 hidden max-w-[700px] text-[18px] leading-[1.5] text-ink/90 text-balance lg:block">
                {conDestacado ? TEXTOS_SECCION.entradillaDestacadoEscritorio : TEXTOS_SECCION.entradillaReposoEscritorio}
            </p>
            <p className="m-0 text-[16px] leading-[1.5] text-ink/90 text-balance lg:hidden">
                {conDestacado ? TEXTOS_SECCION.entradillaDestacadoMovil : TEXTOS_SECCION.entradillaReposoMovil}
            </p>
        </div>
    );
}

// El Escape es de la Biblioteca si nadie lo ha usado ya y el foco está en ella o en ninguna parte
// (tras un clic en un sector, el foco vuelve al body). Así el Escape que cierra el panel de la
// cabecera o el índice móvil no suelta, de paso, la función fijada.
function escapeParaLaSeccion(evento: globalThis.KeyboardEvent, seccion: HTMLElement | null): boolean {
    if (evento.key !== 'Escape' || evento.defaultPrevented) return false;
    const foco = document.activeElement;
    return !foco || foco === document.body || Boolean(seccion?.contains(foco));
}

/**
 * Escape suelta la función fijada. Escucha en el documento y solo mientras hay algo fijado:
 * tras fijar con un clic en un sector el foco no entra en la sección (un trazado SVG no es
 * enfocable), así que un manejador en la sección no oiría la tecla.
 */
function useEscapeSuelta(fijado: boolean, setFijo: (indice: number) => void, seccionRef: RefObject<HTMLElement | null>) {
    useEffect(() => {
        if (!fijado) return;
        const alTeclear = (evento: globalThis.KeyboardEvent) => {
            if (escapeParaLaSeccion(evento, seccionRef.current)) setFijo(-1);
        };
        document.addEventListener('keydown', alTeclear);
        return () => document.removeEventListener('keydown', alTeclear);
    }, [fijado, setFijo, seccionRef]);
}

/**
 * La biblioteca de la memoria de la paciente de ejemplo (UI-SPEC §Interaction 2). Isla de
 * cliente (D-07): el estado (hover, foco y fijado) vive aquí y la función activa se deriva en
 * cada render, nunca se copia de las props. Los dos árboles comparten el estado y los
 * manejadores de la sección; el árbol oculto no recibe eventos y no tiene escuchas propias.
 */
export default function Biblioteca({ destacado, idSeccion }: PropiedadesBiblioteca) {
    const idBase = useId();
    const seccionRef = useRef<HTMLElement>(null);
    const [sobre, setSobre] = useState(-1);
    const [foco, setFoco] = useState(-1);
    const [fijo, setFijo] = useState(-1);
    const reducido = usePrefiereMovimientoReducido();
    const indiceDest = indiceDestacado(destacado);
    // En táctil no hay hover: el árbol móvil usa la activa sin él.
    const activaSinSobre = resolverActiva({ sobre: -1, foco, fijo, destacado: indiceDest });
    const activa = resolverActiva({ sobre, foco, fijo, destacado: indiceDest });
    const pulsada = fijo >= 0 ? fijo : indiceDest;

    function alPulsar(evento: MouseEvent<HTMLElement>) {
        const k = funcionDelClic(evento.target, evento.clientX, evento.clientY);
        setFijo((actual) => alternarFijo(actual, k));
    }

    useEscapeSuelta(fijo >= 0, setFijo, seccionRef);

    return (
        <section
            ref={seccionRef}
            id={idSeccion}
            aria-labelledby={`${idBase}-titulo`}
            onClick={alPulsar}
            onFocus={(evento: FocusEvent<HTMLElement>) => setFoco(funcionConFocoVisible(evento.target))}
            onBlur={() => setFoco(-1)}
            className="relative flex scroll-mt-28 flex-col items-stretch px-4 pb-14 pt-12 text-ink lg:items-center lg:px-16 lg:pb-[72px] lg:pt-16"
        >
            <Cabecera idTitulo={`${idBase}-titulo`} destacado={indiceDest} />
            <EscenarioBibliotecaEscritorio
                activa={activa}
                pulsada={pulsada}
                idBase={idBase}
                reducido={reducido}
                activaSinSobre={activaSinSobre}
                alCambiarSobre={setSobre}
            />
            <EscenarioBibliotecaMovil activa={activaSinSobre} pulsada={pulsada} idBase={idBase} reducido={reducido} />
        </section>
    );
}
